/**
 * Scout skills library — S3 path helpers, allowlisted file types, and pack format.
 *
 * Layout:
 *   GLOBAL → skills/global/{skillId}/
 *   TENANT → skills/{tenantId}/{skillId}/
 *
 * Who can see the skill stays in Postgres. The object key does not encode audience.
 */

export const SKILL_S3_ROOT = "skills/";
export const SKILL_GLOBAL_SEGMENT = "global";
export const SKILL_ENTRYPOINT = "SKILL.md";

/** Extensions allowed for skill tree files (including SKILL.md). */
export const SKILL_ALLOWED_EXTENSIONS = [".md", ".txt", ".json", ".yaml", ".yml", ".csv"] as const;

export type SkillAllowedExtension = (typeof SKILL_ALLOWED_EXTENSIONS)[number];

export const SKILL_MAX_FILES = 40;
export const SKILL_MAX_FILE_BYTES = 256 * 1024;
export const SKILL_MAX_TREE_BYTES = 1024 * 1024;

export type SkillStorageScope = "GLOBAL" | "TENANT";

/**
 * Canonical S3 prefix for a skill (trailing slash).
 * Audience (only-me vs shared) is DB-only and does not change the path.
 */
export function skillStoragePrefix(params: {
  skillId: string;
  scope: SkillStorageScope;
  tenantId?: string | null;
}): string {
  const skillId = params.skillId.trim();
  if (!skillId) {
    throw new Error("skillId is required for skill storage prefix");
  }

  if (params.scope === "GLOBAL") {
    return `${SKILL_S3_ROOT}${SKILL_GLOBAL_SEGMENT}/${skillId}/`;
  }

  const tenantId = params.tenantId?.trim();
  if (!tenantId) {
    throw new Error("tenantId is required for TENANT skill storage prefixes");
  }

  return `${SKILL_S3_ROOT}${tenantId}/${skillId}/`;
}

export function isSkillAllowedRelativePath(relativePath: string): boolean {
  const normalized = normalizeSkillRelativePath(relativePath);
  if (!normalized) {
    return false;
  }
  const lower = normalized.toLowerCase();
  return SKILL_ALLOWED_EXTENSIONS.some((ext) => lower.endsWith(ext));
}

/**
 * Normalize a relative path under a skill prefix.
 * Rejects absolute paths, `..`, empty segments, and leading slashes.
 */
export function normalizeSkillRelativePath(relativePath: string): string | null {
  const trimmed = relativePath.trim().replace(/\\/g, "/");
  if (!trimmed || trimmed.startsWith("/") || trimmed.includes("\0")) {
    return null;
  }
  const parts = trimmed.split("/").filter((part) => part.length > 0);
  if (parts.length === 0 || parts.some((part) => part === "." || part === "..")) {
    return null;
  }
  return parts.join("/");
}

export function skillObjectKey(storagePrefix: string, relativePath: string): string {
  const normalized = normalizeSkillRelativePath(relativePath);
  if (!normalized) {
    throw new Error(`Invalid skill relative path: ${relativePath}`);
  }
  if (!isSkillAllowedRelativePath(normalized)) {
    throw new Error(`Skill file type not allowed: ${normalized}`);
  }
  const prefix = storagePrefix.endsWith("/") ? storagePrefix : `${storagePrefix}/`;
  return `${prefix}${normalized}`;
}

export type SkillTreeEntry = {
  /** Path relative to the skill prefix (e.g. SKILL.md, references/foo.md). */
  path: string;
  size: number;
  updatedAt?: Date;
  etag?: string;
};

/** One file in a skill tree, as listed for an agent to read on demand. */
export type SkillFileIndexEntry = {
  path: string;
  size: number;
};

/** A skill's SKILL.md plus an index of its other files (contents not loaded). */
export type SkillEntrypoint = {
  content: string;
  files: SkillFileIndexEntry[];
};
